import { validIdentity } from './types';
import type { ObservationIdentity, ResolvedScope } from './types';
export type FilterValue = null | boolean | number | string | readonly FilterValue[] | { readonly [key: string]: FilterValue };
function stable(value: FilterValue): string {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return JSON.stringify(value);
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('Non-finite filter');
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index++) {
      if (!Object.prototype.hasOwnProperty.call(value, index)) throw new Error('Sparse filter array');
    }
    return `[${value.map(stable).join(',')}]`;
  }
  if (typeof value !== 'object' || value === null || Object.getPrototypeOf(value) !== Object.prototype) throw new Error('Unsupported filter');
  const obj = value as { readonly [key: string]: FilterValue };
  return `{${Object.keys(obj).sort().map(key => `${JSON.stringify(key)}:${stable(obj[key])}`).join(',')}}`;
}
export function scopeObservationKey(identity: ObservationIdentity, scope: ResolvedScope, source: string,
  filters: FilterValue = null): readonly string[] {
  if (!validIdentity(identity) || !source.trim() || !['all', 'environment'].includes(scope.kind)) throw new Error('Resolved observation identity required');
  const scopeKey = scope.kind === 'environment' ? `environment:${scope.environment.environmentId}` : 'all';
  return Object.freeze(['scope-observation-v1', identity.principalKey, identity.sessionEpoch,
    identity.permissionEpoch, scopeKey, source, stable(filters)]);
}
export class StaleObservationError extends Error {
  constructor() { super('Observation belongs to an obsolete generation'); this.name = 'StaleObservationError'; }
}
export interface ReadTicket { readonly signal: AbortSignal; readonly generation: number }
export function createReadGeneration() {
  let generation = 0;
  let controller = new AbortController();
  const issued = new WeakSet<ReadTicket>();
  return {
    capture(): ReadTicket {
      const ticket = Object.freeze({ signal: controller.signal, generation });
      issued.add(ticket);
      return ticket;
    },
    advance(): void { controller.abort(); generation++; controller = new AbortController(); },
    assertCurrent(ticket: ReadTicket): void {
      if (!issued.has(ticket) || ticket.generation !== generation || ticket.signal.aborted) throw new StaleObservationError();
    },
  };
}
